import { balanceSignOf, storedEntryType } from "./entry-type";
import { EXPENSE_TYPE } from "./expense";
import { toCents } from "./money";
import { spentAtOf } from "./spent-at";
import { isSplitMode, retainedSplit } from "./split";
import { findTag, nextTagColor, tagKey, uniqueTagNames } from "./tags";

export const outboxKinds = {
  createExpense: "createExpense",
  updateExpense: "updateExpense",
  deleteExpense: "deleteExpense",
  deleteParticipant: "deleteParticipant",
  renameGroup: "renameGroup",
};

const TEMP_ID_PREFIX = "tmp-";
const PENDING_TAG_PREFIX = "pending-tag-";
const RETRY_BASE_MS = 2000;
const RETRY_MAX_MS = 5 * 60 * 1000;

export const PENDING_TEXT = "wird gesendet";

export const PENDING_GROUP_TEXT =
  "Enthält Änderungen, die noch gesendet werden.";

export const QUEUED_SUFFIX = "wird gesendet, sobald du wieder online bist";

export const queuedText = (done) => `${done} – ${QUEUED_SUFFIX}`;

export const isTempId = (id) =>
  typeof id === "string" && id.startsWith(TEMP_ID_PREFIX);

export const tempIdOf = (entry) => `${TEMP_ID_PREFIX}${entry.key}`;

const sameId = (a, b) => `${a}` === `${b}`;

export const createOutboxEntry = ({
  key,
  kind,
  groupId,
  targetId = null,
  body,
  rejectedText,
  holdUntil,
  now = new Date(),
}) => ({
  key,
  kind,
  groupId,
  targetId,
  body,
  rejectedText,
  createdAt: now.toISOString(),
  attempts: 0,
  retryAt: 0,
  ...(holdUntil == null ? {} : { holdUntil }),
});

export const hasHold = (entry) => typeof entry?.holdUntil === "number";

export const isHeld = (entry, now = Date.now()) =>
  hasHold(entry) && entry.holdUntil > now;

const withoutHold = ({ holdUntil, ...entry }) => entry;

export const releaseHold = (outbox = [], key) =>
  outbox.map((entry) =>
    entry.key === key && hasHold(entry) ? withoutHold(entry) : entry,
  );

export const releaseAllHolds = (outbox = []) =>
  outbox.some(hasHold)
    ? outbox.map((entry) => (hasHold(entry) ? withoutHold(entry) : entry))
    : outbox;

export const onlyHeldEntries = (outbox = []) => outbox.every(hasHold);

export const undoEntry = (outbox = [], key, restored = null) => {
  const remaining = outbox.filter((entry) => entry.key !== key);
  if (!restored?.entry) return remaining;
  if (remaining.some((entry) => entry.key === restored.entry.key)) {
    return remaining;
  }
  const index = Math.min(Math.max(0, restored.index ?? 0), remaining.length);
  return [
    ...remaining.slice(0, index),
    restored.entry,
    ...remaining.slice(index),
  ];
};

export const requestOf = ({ kind, groupId, targetId, body }) => {
  const group = `/groups/${groupId}`;
  switch (kind) {
    case outboxKinds.createExpense:
      return { method: "POST", path: `${group}/expenses`, body };
    case outboxKinds.updateExpense:
      return { method: "PUT", path: `${group}/expenses/${targetId}`, body };
    case outboxKinds.deleteExpense:
      return { method: "DELETE", path: `${group}/expenses/${targetId}` };
    case outboxKinds.deleteParticipant:
      return { method: "DELETE", path: `${group}/participants/${targetId}` };
    case outboxKinds.renameGroup:
      return { method: "POST", path: group, body };
    default:
      throw new Error(`Unknown outbox entry ${kind}`);
  }
};

export const findCreateEntry = (outbox, tempId) =>
  outbox.find(
    (entry) =>
      entry.kind === outboxKinds.createExpense && tempIdOf(entry) === tempId,
  );

// A change of an entry that was never sent rewrites or drops its creation.
export const addToOutbox = (outbox, entry) => {
  const create = isTempId(entry.targetId)
    ? findCreateEntry(outbox, entry.targetId)
    : undefined;
  if (create && entry.kind === outboxKinds.updateExpense) {
    return outbox.map((e) =>
      e === create
        ? { ...e, body: entry.body, rejectedText: entry.rejectedText }
        : e,
    );
  }
  if (create && entry.kind === outboxKinds.deleteExpense) {
    return outbox.filter((e) => e !== create);
  }
  return [...outbox, entry];
};

export const retryDelay = (attempts) =>
  Math.min(RETRY_BASE_MS * 2 ** Math.max(0, attempts - 1), RETRY_MAX_MS);

const relativeShare = (share, amount) =>
  amount ? Math.round((share * 10000) / amount) : 0;

export const isPendingTagId = (id) =>
  typeof id === "string" && id.startsWith(PENDING_TAG_PREFIX);

const resolvePendingTags = (groupTags, names) => {
  let tags = groupTags ?? [];
  const resolved = uniqueTagNames(names).map((name) => {
    let tag = findTag(tags, name);
    if (!tag) {
      tag = {
        id: `${PENDING_TAG_PREFIX}${tagKey(name)}`,
        name,
        color: nextTagColor(tags.map((t) => t.color)),
        pending: true,
      };
      tags = [...tags, tag];
    }
    return { id: tag.id, name: tag.name, color: tag.color };
  });
  return { resolved, tags };
};

const withTags = (group, entry) => {
  const names = entry.body?.expense?.tags;
  if (!Array.isArray(names)) return { group, tags: undefined };
  const { resolved, tags } = resolvePendingTags(group.tags, names);
  return { group: { ...group, tags }, tags: resolved };
};

const descriptionOf = (expense, previous) => {
  if (expense.description === undefined) return previous.description ?? null;
  return expense.description?.trim() || null;
};

const keptSplit = (expense, previous) =>
  retainedSplit(
    previous.split_mode,
    (previous.receivers ?? []).map((r) => ({
      id: r.participant_id,
      value: r.relative_share_in_decimals,
    })),
    expense.amount,
    expense.receivers,
  );

const pendingSplit = (expense, type, previous) => {
  if (type === EXPENSE_TYPE.payment) return null;
  if (expense.split_mode === undefined) return keptSplit(expense, previous);
  return isSplitMode(expense.split_mode)
    ? {
        mode: expense.split_mode,
        values: expense.receivers.map((r) => r.split_value),
      }
    : null;
};

const toPendingExpense = (entry, id, previous = {}, tags = undefined) => {
  const { expense } = entry.body;
  const type = storedEntryType(
    expense.type ?? EXPENSE_TYPE.expense,
    previous.type,
  );
  const split = pendingSplit(expense, type, previous);
  const name =
    type === EXPENSE_TYPE.payment && !expense.title?.trim()
      ? "Zahlung"
      : expense.title;
  return {
    currency: null,
    group_id: entry.groupId,
    created_at: entry.createdAt,
    ...previous,
    id,
    name,
    type,
    amount_in_decimals: expense.amount,
    payer: Number(expense.from.id),
    description: descriptionOf(expense, previous),
    split_mode: split?.mode ?? null,
    updated_at: entry.createdAt,
    receivers: expense.receivers.map((receiver, index) => ({
      participant_id: Number(receiver.id),
      absolute_share_in_decimals: receiver.share,
      relative_share_in_decimals: split
        ? split.values[index]
        : relativeShare(receiver.share, expense.amount),
    })),
    spent_at: expense.spent_at ?? spentAtOf(previous) ?? entry.createdAt,
    tags: tags ?? previous.tags ?? [],
    pending: true,
  };
};

const shiftBalances = (participants, expense, direction) =>
  participants.map((participant) => {
    const sign = direction * balanceSignOf(expense.type);
    const paid = sameId(participant.id, expense.payer)
      ? toCents(expense.amount_in_decimals)
      : 0;
    const share = (expense.receivers || [])
      .filter((r) => sameId(r.participant_id, participant.id))
      .reduce((sum, r) => sum + toCents(r.absolute_share_in_decimals), 0);
    if (!paid && !share) return participant;
    return {
      ...participant,
      gets: toCents(participant.gets) + sign * paid,
      pays: toCents(participant.pays) + sign * share,
    };
  });

const applyEntry = (group, entry) => {
  const previous = group.expenses.find((e) => sameId(e.id, entry.targetId));
  switch (entry.kind) {
    case outboxKinds.createExpense: {
      const tagged = withTags(group, entry);
      const expense = toPendingExpense(entry, tempIdOf(entry), {}, tagged.tags);
      return {
        ...tagged.group,
        expenses: [expense, ...group.expenses],
        participants: shiftBalances(group.participants, expense, 1),
      };
    }
    case outboxKinds.updateExpense: {
      if (!previous) return group;
      const tagged = withTags(group, entry);
      const expense = toPendingExpense(entry, previous.id, previous, tagged.tags);
      return {
        ...tagged.group,
        expenses: group.expenses.map((e) => (e === previous ? expense : e)),
        participants: shiftBalances(
          shiftBalances(group.participants, previous, -1),
          expense,
          1,
        ),
      };
    }
    case outboxKinds.deleteExpense:
      if (!previous) return group;
      return {
        ...group,
        expenses: group.expenses.filter((e) => e !== previous),
        participants: shiftBalances(group.participants, previous, -1),
      };
    case outboxKinds.deleteParticipant:
      return {
        ...group,
        participants: group.participants.filter(
          (p) => !sameId(p.id, entry.targetId),
        ),
      };
    case outboxKinds.renameGroup:
      return { ...group, name: entry.body.name.trim() };
    default:
      return group;
  }
};

export const pendingEntriesOf = (outbox = [], groupId) =>
  outbox.filter((entry) => sameId(entry.groupId, groupId));

const latest = (a, b) => (a === null || Date.parse(b) > Date.parse(a) ? b : a);

const withTagStats = (group) => {
  if (!Array.isArray(group.tags)) return group;
  const tags = group.tags.map((tag) => {
    const used = group.expenses.filter((expense) =>
      (expense.tags ?? []).some((t) => sameId(t.id, tag.id)),
    );
    return {
      ...tag,
      usage: used.length,
      last_used_at: used.map(spentAtOf).reduce(latest, null),
    };
  });
  return { ...group, tags };
};

export const applyPending = (group, outbox = []) => {
  if (!group) return group;
  const entries = pendingEntriesOf(outbox, group.id);
  if (entries.length === 0) return group;
  const applied = entries.reduce(applyEntry, {
    ...group,
    expenses: group.expenses || [],
    participants: group.participants || [],
  });
  return { ...withTagStats(applied), pendingChanges: entries.length };
};

export const applyPendingNames = (groups = [], outbox = []) => {
  const renames = outbox.filter((e) => e.kind === outboxKinds.renameGroup);
  if (renames.length === 0) return groups;
  return groups.map((group) =>
    renames
      .filter((e) => sameId(e.groupId, group.id))
      .reduce((g, e) => ({ ...g, name: e.body.name.trim() }), group),
  );
};
